/**
 * Walkthrough Tab Highlighting Utilities
 * Functions to highlight and unhighlight navigation tabs during walkthrough
 */

/**
 * Highlight a specific navigation tab
 * @param {string} tabName - Name of the tab to highlight ('Members', 'Jobs', 'Forum')
 * @param {boolean} isMobile - Whether we're on mobile view
 * @returns {HTMLElement|null} - The highlighted element or null if not found
 */
export const highlightTab = (tabName, isMobile = false) => {
  if (!tabName) return null;

  // Find the tab element using data attribute
  const selector = `[data-walkthrough-tab="${tabName}"]`;
  const element = document.querySelector(selector);

  if (element) {
    // Add highlight class (different for mobile)
    const highlightClass = isMobile ? 'walkthrough-highlight-mobile' : 'walkthrough-highlight';
    element.classList.add(highlightClass);
    
    // Elevate z-index to appear above backdrop
    element.style.zIndex = isMobile ? '9999' : '9997';
    element.style.position = 'relative';
    
    return element;
  }

  console.warn(`Walkthrough: Could not find tab "${tabName}" to highlight`);
  return null;
};

/**
 * Highlight a mobile navigation tab
 * @param {string} tabName - Name of the tab to highlight
 * @returns {HTMLElement|null} - The highlighted element or null if not found
 */
export const highlightMobileTab = (tabName) => {
  return highlightTab(tabName, true);
};

/**
 * Remove highlight from all navigation tabs
 */
export const removeAllHighlights = () => {
  const highlightedElements = document.querySelectorAll('.walkthrough-highlight, .walkthrough-highlight-mobile');
  
  highlightedElements.forEach(el => {
    el.classList.remove('walkthrough-highlight');
    el.classList.remove('walkthrough-highlight-mobile');
    el.style.zIndex = '';
    el.style.position = '';
  });
};

/**
 * Remove highlight from a specific tab
 * @param {string} tabName - Name of the tab to unhighlight
 */
export const removeTabHighlight = (tabName) => {
  if (!tabName) return;

  const selector = `[data-walkthrough-tab="${tabName}"]`;
  const element = document.querySelector(selector);

  if (element) {
    element.classList.remove('walkthrough-highlight');
    element.style.zIndex = '';
    element.style.position = '';
  }
};

/**
 * Check if we're on mobile view
 * @returns {boolean}
 */
export const isMobileView = () => {
  return window.innerWidth < 768; // md breakpoint
};
